import * as React from "react";
import { useQuery } from "react-query";
import { CharactersChargedByMtService, MetricResult, MetricsService } from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { DataTable } from "components/DataTable/DataTable";
import { Tooltip, Typography } from "@mui/material";
import { secondsToMinutes } from "utils/dateUtils";

interface Props {
  clientId: string;
  from: Date;
  to: Date;
  onLoadingStateChange?: (isLoading: boolean) => void;
}

const ClientContactMtRequestsTable: React.FC<Props> = ({ clientId, from, to, onLoadingStateChange }) => {
  const fromISO = React.useMemo(() => from.toISOString(), [from]);
  const toISO = React.useMemo(() => to.toISOString(), [to]);

  const { data, isLoading, isFetching, error } = useQuery(["listMtRequestCounts", clientId, fromISO, toISO], {
    queryFn: () =>
      MetricsService.getRequestCounts({
        clientId,
        startTime: fromISO,
        endTime: toISO
      }),
    cacheTime: 0,
    enabled: !!clientId
  });

  React.useEffect(() => {
    onLoadingStateChange?.(isLoading || isFetching);
  }, [isFetching, isLoading, onLoadingStateChange]);

  const stats = React.useMemo(
    () => data?.results?.sort((r1, r2) => r2.values[0].reqCount - r1.values[0].reqCount) || [],
    [data?.results]
  );

  const charactersChargedCell = React.useCallback((m: MetricResult) => {
    const { charactersCharged } = m.values[0];
    const tooltipItems: string[] = Object.keys(charactersCharged)
      .filter(serviceId => ["AZURE", "INTENTO"].includes(serviceId))
      .map(serviceId => `${serviceId}: ${charactersCharged[serviceId as keyof CharactersChargedByMtService] || 0}`);

    return (
      <Tooltip title={tooltipItems.join(", ")}>
        <Typography
          variant="body2"
          component="span"
          sx={{
            textDecoration: "underline",
            textDecorationStyle: "dotted", // Apply dotted style
            textDecorationColor: "currentcolor", // Use the current text color
            textDecorationThickness: "2px", // Set the thickness of the underline
            textUnderlineOffset: "3px" // Adjust the offset of the underline
          }}
        >
          {(charactersCharged.AZURE || 0) + (charactersCharged.INTENTO || 0)}
        </Typography>
      </Tooltip>
    );
  }, []);

  if (isLoading) {
    return <PageLoading />;
  }
  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  return (
    <DataTable
      columns={[
        {
          id: "username",
          title: "Username",
          cell: (m: MetricResult) => m.label
        },
        {
          id: "count",
          title: "Request Count",
          cell: (m: MetricResult) => m.values[0].reqCount
        },
        {
          id: "charactersCharged",
          title: "Characters Charged",
          cell: charactersChargedCell
        },
        {
          id: "textCharactersCharged",
          title: "Text Translation (characters)",
          cell: (m: MetricResult) => m.values[0].charactersChargedByUserRequest?.TEXT_TRANSLATION || 0
        },
        {
          id: "documentCharactersCharged",
          title: "Document Translation (characters)",
          cell: (m: MetricResult) => m.values[0].charactersChargedByUserRequest?.FILES_TRANSLATION || 0
        },
        {
          id: "mediaTranscriptionDuration",
          title: "Transcription (minutes)",
          cell: (m: MetricResult) => secondsToMinutes(m.values[0].secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0)
        },
        {
          id: "mediaTranscriptionAndTranslationDuration",
          title: "Transcription And Translation (minutes)",
          cell: (m: MetricResult) => secondsToMinutes(m.values[0].secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0)
        },
        {
          id: "mediaTransclationAndDubbingDuration",
          title: "Translation And Dubbing (minutes)",
          cell: (m: MetricResult) => secondsToMinutes(m.values[0].secondsChargedByUserRequest?.MEDIA_DUBBING || 0)
        },
        {
          id: "mediaTranslationAndSubtitlingDuration",
          title: "Translation And Subtitling (minutes)",
          cell: (m: MetricResult) =>
            secondsToMinutes(m.values[0].secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0)
        }
      ]}
      data={stats}
      rowKey={(m: MetricResult) => m.id}
    />
  );
};

export { ClientContactMtRequestsTable };
